import { useFrame, useThree } from '@react-three/fiber'
import isNil from 'lodash/isNil'
import React, { FC, useMemo, useRef } from 'react'
import { MathUtils, PerspectiveCamera, Vector3 } from 'three'

interface FlightHandlerProps {
  coordinateToFlyTo: Vector3 | undefined
  coordinatesToLookAtAfterFlight?: Vector3 | undefined
}

/**
 * FlightHandler manages smooth camera animations when flying to a specific coordinate in 3D space.
 * The camera smoothly moves to a position above and behind the target coordinate while
 * transitioning its focus to the target.
 */
export const FlightHandler: FC<FlightHandlerProps> = ({
  coordinateToFlyTo,
  coordinatesToLookAtAfterFlight,
}) => {
  const { camera } = useThree()

  const flightRoute = useRef<
    | {
        start: {
          pos: Vector3
          lookAt: Vector3
        }
        end: {
          pos: Vector3
          lookAt: Vector3
        }
      }
    | undefined
  >(undefined)
  const flightProgress = useRef(0)

  // Add refs for reusable Vector3 objects
  const currentPosRef = useRef(new Vector3())
  const currentLookAtRef = useRef(new Vector3())

  // Define the flight route when coordinate changes
  useMemo(() => {
    if (!coordinateToFlyTo) {
      flightRoute.current = undefined
      flightProgress.current = 0
      return
    }

    const targetPosition = coordinateToFlyTo
    const cameraAsPerspective = camera as PerspectiveCamera

    const start = {
      pos: cameraAsPerspective.position.clone(),
      // Store the current look-at point by extending the camera's forward vector
      lookAt: new Vector3(0, 0, -1)
        .applyQuaternion(camera.quaternion)
        .multiplyScalar(100)
        .add(camera.position),
    }

    const cameraFovInRads = !cameraAsPerspective.fov
      ? undefined
      : MathUtils.degToRad(cameraAsPerspective.fov)

    if (isNil(cameraFovInRads)) {
      throw new Error(
        'Camera FOV is undefined in flight handler -- this is likely because a new camera type was introduced'
      )
    }

    const cameraAspect = cameraAsPerspective.aspect

    if (isNil(cameraAspect)) {
      throw new Error(
        'Camera aspect is undefined in flight handler -- this is likely because a new camera type was introduced'
      )
    }

    // Use a default offset distance for the camera
    const defaultOffset = 2.6 // meters
    const cameraOffset =
      defaultOffset /
      Math.tan((cameraAsPerspective.fov * Math.PI) / 180 / 2) /
      cameraAsPerspective.aspect

    // Position camera at a fixed distance from target
    const cameraPosition = new Vector3(
      targetPosition.x, // Same X as target
      targetPosition.y + cameraOffset, // Move up on Y
      targetPosition.z + cameraOffset // Move back on Z
    )

    const finalLookAtTarget =
      coordinatesToLookAtAfterFlight || coordinateToFlyTo

    const end = {
      pos: cameraPosition,
      lookAt: finalLookAtTarget.clone(),
    }

    flightRoute.current = { start, end }
    flightProgress.current = 0
  }, [coordinateToFlyTo, coordinatesToLookAtAfterFlight, camera])

  useFrame(({ camera }, delta) => {
    // Not currently flying
    if (!coordinateToFlyTo || !flightRoute.current) return

    // Flight finished
    if (flightProgress.current >= 1) {
      flightRoute.current = undefined
      flightProgress.current = 0
      return
    }

    // In flight
    flightProgress.current = Math.min(flightProgress.current + 0.8 * delta, 1)
    const smoothT = MathUtils.smoothstep(flightProgress.current, 0, 1)

    // Smoothly interpolate position
    currentPosRef.current.lerpVectors(
      flightRoute.current.start.pos,
      flightRoute.current.end.pos,
      smoothT
    )
    camera.position.copy(currentPosRef.current)

    // Smoothly interpolate the look-at target
    currentLookAtRef.current.lerpVectors(
      flightRoute.current.start.lookAt,
      flightRoute.current.end.lookAt,
      smoothT
    )
    camera.lookAt(currentLookAtRef.current)
  })

  return <></>
}
